// Static preview data - pulled from real, one-off read-only audit runs
// against oh_admin_prod (Bangalore preschool centres only). Nothing here is
// live in the request sense: the underlying row-level JSON for each date was
// generated ahead of time by `audit_parent_updates.py --json-output` in the
// centre-toolkit repo and copied into admin-portal's `public/audit-data/`.
// This page fetches those static files at runtime (see rowsUrlForDate below)
// rather than importing them as JS - that's what makes the date picker
// possible without bloating the app bundle with every day's ~800KB of rows.
//
// Each date's file is shaped { rows, absentees }:
//   - rows: one entry per (present child, required update) - what "sent
//     on time / late / missing" etc. are computed from.
//   - absentees: one entry per child who was scheduled but marked absent
//     that day - no update requirements apply to them, so they're tracked
//     separately rather than as "missing" rows.
// Rooms are deliberately not part of either shape - not relevant to this
// audit, only which centre and which child.
//
// To add a new date: re-run
//   python3 scripts/audit_parent_updates.py --date YYYY-MM-DD \
//     --json-output docs/audits/parent-updates-YYYY-MM-DD.json
// in centre-toolkit, copy the JSON into this app's
// public/audit-data/parent-updates-YYYY-MM-DD.json, and add the date to
// public/audit-data/manifest.json.
//
// Weekends are deliberately excluded from the manifest: centres don't run
// sessions on Saturday/Sunday, so there's no expectation of updates and
// nothing meaningful to audit - don't add Sat/Sun dates here.
//
// Salt Lake and Theatre Road are excluded from every date: confirmed via a
// 60-day check that neither centre has ever logged a food/nap/toilet moment
// (only Learning Moments) - they don't run the full-day preschool/daycare
// program these guidelines describe, so the checklist doesn't apply there.

export const MANIFEST_URL = '/audit-data/manifest.json'

export const rowsUrlForDate = (date) => `/audit-data/parent-updates-${date}.json`

// Trends is a separate, much smaller file: per-centre {onTime,late,missing,
// total} for each date, with no row-level detail (no messages, no per-child
// data). Covers a longer window (currently 22 weekdays, ~30 calendar days)
// than the date-picker's 7 fully-detailed dates, since a trend needs more
// history than a drill-down does, and shipping full per-child detail for a
// month would be tens of MB for no reason - see trends.json's generation
// notes in this repo's PR/commit history if regenerating.
export const TRENDS_URL = '/audit-data/trends.json'

// Same idea as trends.json, but grouped by teacher (postedBy) instead of
// centre: {dates, teachers (sorted, every teacher who posted at least once
// in the window), byDate: {date: {teacherName: {posted,late,generic}}}}.
// Unlike centres, teachers have no "total required" ground truth (no
// room-to-teacher assignment table - see teacherRollupNote below), so every
// metric here is a rate among what they *did* post, not against a required
// count.
export const TEACHER_TRENDS_URL = '/audit-data/teacherTrends.json'

export const TEACHER_TREND_METRICS = [
  { key: 'late', label: 'Late', higherIsBetter: false },
  { key: 'generic', label: 'Non-personalised', higherIsBetter: false }
]

// One teacher's series for a given rate metric (late/generic) as a % of
// what they posted that day, across every date in teacherTrends.json. A
// date is skipped (not zeroed) if the teacher posted nothing that day.
export const computeTeacherTrend = (trendsData, teacher, metricKey) =>
  trendsData.dates
    .map((date) => {
      const t = trendsData.byDate[date]?.[teacher]
      if (!t || t.posted === 0) return null
      return {
        date,
        posted: t.posted,
        late: t.late,
        generic: t.generic,
        total: t.posted,
        value: t[metricKey],
        pct: (t[metricKey] / t.posted) * 100
      }
    })
    .filter(Boolean)

export const teacherRollupNote =
  'Only covers updates that were posted - missing updates (nobody posted anything for that child/slot) aren’t attributed to a teacher here. There’s no room-to-teacher assignment table in the schema to attribute a miss reliably; use the centre or teacher drill-downs to see missing updates directly.'

export const STAT_EXPLANATIONS = {
  'sent late':
    'The update was posted after its deadline: 14:00 IST for Morning Snack/Lunch/Care Routine, 17:00 IST for Learning Moments and Evening Snack. Click a row\'s "Sent at / deadline" column to see exactly how late.',
  'flagged non-personalised':
    'The exact same message (one moment/post, one piece of text) was linked to more than one present child at once - e.g. a teacher selected a whole room and wrote one update for all of them, rather than one per child. Not a text-similarity guess: it\'s literally one written message reaching several children. Click through to see the actual message text.'
}

// Pure aggregation, computed from whichever date's rows are currently
// loaded - never hand-typed, so it can't drift out of sync with the
// row-level detail the way earlier hand-maintained summaries did.
export const computeOverallStats = (rows) => [
  { n: rows.length.toLocaleString(), l: 'updates required' },
  { n: String(rows.filter((r) => r.status === 'ON_TIME').length), l: 'sent on time' },
  { n: String(rows.filter((r) => r.status === 'LATE').length), l: 'sent late' },
  { n: String(rows.filter((r) => r.status === 'MISSING').length), l: 'never sent' },
  { n: String(rows.filter((r) => r.generic).length), l: 'flagged non-personalised' }
]

export const computeCentreOverview = (rows) => {
  const byCentre = {}
  rows.forEach((r) => {
    const c = (byCentre[r.centre] = byCentre[r.centre] || { centre: r.centre, onTime: 0, late: 0, missing: 0, total: 0 })
    c.total += 1
    if (r.status === 'ON_TIME') c.onTime += 1
    if (r.status === 'LATE') c.late += 1
    if (r.status === 'MISSING') c.missing += 1
  })
  return Object.values(byCentre).sort((a, b) => a.centre.localeCompare(b.centre))
}

export const computeTeacherRollup = (rows) => {
  const byTeacher = {}
  rows.forEach((r) => {
    if (!r.postedBy) return
    const t = (byTeacher[r.postedBy] = byTeacher[r.postedBy] || { name: r.postedBy, posted: 0, late: 0, generic: 0 })
    t.posted += 1
    if (r.status === 'LATE') t.late += 1
    if (r.generic) t.generic += 1
  })
  return Object.values(byTeacher).sort((a, b) => (b.late + b.generic) - (a.late + a.generic))
}

// Fixed column order for the per-child view - mirrors UPDATE_LABELS in
// audit_parent_updates.py. A child only has a 'pm_snack' entry at all if
// they were extended-day that date (see required_updates_for in the script);
// everyone else simply has no key for it, rendered as "n/a" below.
export const UPDATE_COLUMNS = [
  { key: 'am_snack', label: 'Morning Snack' },
  { key: 'lunch', label: 'Lunch' },
  { key: 'care_routine', label: 'Care Routine' },
  { key: 'learning', label: 'Learning Moments' },
  { key: 'pm_snack', label: 'Evening Snack' }
]

// One row per child (present or absent) for a centre, with each required
// update's full result keyed by update type - this is what lets the Centre
// Detail tab show a single row per child instead of one row per update.
export const computeChildMatrix = (rows, absentees) => {
  const byChild = {}
  rows.forEach((r) => {
    const key = `${r.centre}||${r.child}`
    const c = (byChild[key] = byChild[key] || { centre: r.centre, child: r.child, present: true, updates: {} })
    c.updates[r.updateKey] = r
  })
  absentees.forEach((a) => {
    const key = `${a.centre}||${a.child}`
    if (!byChild[key]) byChild[key] = { centre: a.centre, child: a.child, present: false, updates: {} }
  })
  return Object.values(byChild).sort((a, b) => a.child.localeCompare(b.child))
}

// The three per-day metrics trends.json carries per centre. higherIsBetter
// flips both the bar-colour thresholds and how a trend's delta reads as
// "improving" vs "declining" - a rising Missing % is bad, a rising On time %
// is good.
export const TREND_METRICS = [
  { key: 'onTime', label: 'On time', higherIsBetter: true },
  { key: 'missing', label: 'Missing', higherIsBetter: false },
  { key: 'generic', label: 'Non-personalised', higherIsBetter: false }
]

// One centre's series for a given metric (onTime/missing/generic) across
// every date in trends.json, in date order. A date is skipped (not zeroed)
// if the centre had no required updates that day - e.g. a holiday - so a
// gap in the chart reads as "no data" rather than a false 0%.
export const computeCentreTrend = (trendsData, centre, metricKey) =>
  trendsData.dates
    .map((date) => {
      const c = trendsData.byDate[date]?.[centre]
      if (!c || c.total === 0) return null
      return {
        date,
        onTime: c.onTime,
        late: c.late,
        missing: c.missing,
        generic: c.generic,
        total: c.total,
        value: c[metricKey],
        pct: (c[metricKey] / c.total) * 100
      }
    })
    .filter(Boolean)

// Simple, honest "improving or not" signal: mean % of the first half of the
// available dates vs the second half. Deliberately not a linear regression
// or anything that implies more statistical rigor than a 3-4 week static
// preview can support.
export const trendSummary = (series) => {
  if (series.length < 2) return null
  const mid = Math.ceil(series.length / 2)
  const firstHalf = series.slice(0, mid)
  const secondHalf = series.slice(mid)
  const avg = (arr) => arr.reduce((sum, p) => sum + p.pct, 0) / arr.length
  const firstAvg = avg(firstHalf)
  const secondAvg = secondHalf.length ? avg(secondHalf) : firstAvg
  return { firstAvg, secondAvg, delta: secondAvg - firstAvg }
}
